import type { JSX } from 'react';
import { toast } from 'sonner';
import { Copy, ExternalLink, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FolderCampaign } from '@/api/types';

/**
 * The campaigns a folder's creators are being recruited for.
 *
 * The copy button is the point. An outreacher writing a DM needs the
 * creator-facing campaign link in their clipboard, and the alternative is
 * hunting for it in another tab and pasting something slightly wrong.
 *
 * `clipperUrl` is resolved live from clipping on every read and can be null
 * when a campaign has no published page — in that case the button is hidden
 * rather than shown broken. A dead link pasted into a DM to a creator is worse
 * than no link.
 */
export function CampaignBrief({ campaigns }: { campaigns: FolderCampaign[] }): JSX.Element | null {
  if (campaigns.length === 0) return null;

  function copyLink(c: FolderCampaign): void {
    if (!c.clipperUrl) return;
    void navigator.clipboard.writeText(c.clipperUrl);
    toast.success(`Copied the ${c.name} link`);
  }

  return (
    <div>
      <h3 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-primary">
        <Megaphone className="h-3 w-3" strokeWidth={2} />
        {campaigns.length === 1 ? 'Campaign' : 'Campaigns'}
      </h3>
      <ul className="space-y-2">
        {campaigns.map((c) => (
          <li
            key={c.id}
            className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-brand/25 bg-background/60 px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{c.name}</span>
                {c.status === 'pending' && (
                  <span className="rounded-full border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[10px] font-semibold text-warning">
                    Starting soon
                  </span>
                )}
              </div>
              {c.description && (
                <p className="mt-0.5 text-sm leading-snug text-muted-foreground">
                  {c.description}
                </p>
              )}
            </div>
            {c.clipperUrl && (
              <div className="flex shrink-0 items-center gap-1.5">
                <Button size="sm" variant="outline" onClick={() => copyLink(c)}>
                  <Copy className="mr-1 h-3.5 w-3.5" />
                  Copy link
                </Button>
                <Button size="sm" variant="ghost" asChild>
                  <a href={c.clipperUrl} target="_blank" rel="noopener noreferrer" title="Open campaign page">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-1.5 text-xs text-muted-foreground">
        Paste the link into your DM so the creator can see the campaign and sign up.
      </p>
    </div>
  );
}
